import { ContentType } from '@entries/content-types'
import { useOrganization } from '@hooks/use-organization'
import { StructuredData } from '@sd/structured-data'
import { graphql, useStaticQuery } from 'gatsby'
import React, { type FC, useMemo } from 'react'
import {
  type Organization,
  type Product,
  type Review,
  type WithContext,
} from 'schema-dts'

export const CustomersReviewsStructuredData: FC<{
  reviews: Queries.CustomersReviewsQuery['reviews']
}> = ({ reviews }) => {
  const { aggregateRating } = useStaticQuery<Queries.AggregateRatingQuery>(
    graphql`
      query AggregateRating {
        aggregateRating: getNetReviewsAggregateRating {
          ratingValue
          ratingCount
        }
      }
    `
  )

  const organization = useOrganization()

  const { ratingValue = 0, ratingCount = 0 } = aggregateRating

  const allReviews: Review[] = reviews.map((review) => {
    if (review.__typename === ContentType.TrustpilotReview) {
      return {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.stars,
        },
        author: {
          '@type': 'Person',
          name: review.consumer.displayName,
        },
        datePublished: review.createdAt,
        reviewBody: review.text,
      }
    }

    if (review.__typename === ContentType.NetreviewsReview) {
      return {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: parseFloat(review.rate),
        },
        author: {
          '@type': 'Person',
          name: review.firstname,
        },
        datePublished: review.review_date,
        reviewBody: review.review,
      }
    }

    return null
  })

  const firstReview = allReviews[0]

  const dataReviews = useMemo<WithContext<Organization>>(
    () => ({
      // @ts-expect-error
      ...organization,
      '@context': 'https://schema.org',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue,
        ratingCount,
      },
      review: allReviews,
    }),
    [reviews, aggregateRating, organization]
  )

  const dataProduct: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Fortuneo',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue,
      reviewCount: ratingCount,
      worstRating: '1',
      bestRating: '5',
    },
    review: firstReview,
  }

  if (!ratingCount) {
    return null
  }

  return (
    <>
      <StructuredData data={JSON.stringify(dataReviews)} />
      <StructuredData data={JSON.stringify(dataProduct)} />
    </>
  )
}
